import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CheckCircle2, Undo2, AlertTriangle } from "lucide-react";

/* Where an unsubscribe link lands. It runs the opt out immediately rather than asking again,
   because a person who clicked unsubscribe has already decided. The way back is one button. */

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F4F1EA] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-[520px] bg-white border border-[#E5DECE] rounded-2xl p-7 sm:p-9 text-center">
        <img src="/pawlinx-logo-nav.png" alt="PawLinx" className="h-8 w-auto mx-auto mb-6" />
        {children}
      </div>
    </div>
  );
}

export default function UnsubscribePage() {
  const { token } = useParams();
  const [state, setState] = useState("working");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post(`/unsubscribe/${token}`);
        if (!alive) return;
        setEmail(data?.email || "");
        setState("done");
      } catch {
        if (alive) setState("bad");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const undo = async () => {
    setBusy(true);
    try {
      await api.post(`/resubscribe/${token}`);
      setState("undone");
    } catch { /* leave the state as it is */ }
    finally { setBusy(false); }
  };

  if (state === "working") {
    return <Shell><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#667085]" /></Shell>;
  }
  if (state === "bad") {
    return (
      <Shell>
        <AlertTriangle className="w-9 h-9 text-[#BC4749] mx-auto mb-4" />
        <h1 className="text-[21px] font-bold text-[#0B2747] mb-2">This link is not valid</h1>
        <p className="text-[14px] text-[#667085] leading-relaxed">
          It may have been mistyped. Email support@pawlinx.com or call 1-888-827-2717 and we will
          take you off the list by hand.
        </p>
      </Shell>
    );
  }
  if (state === "undone") {
    return (
      <Shell>
        <CheckCircle2 className="w-9 h-9 text-[#0E9F6E] mx-auto mb-4" />
        <h1 className="text-[21px] font-bold text-[#0B2747] mb-2">You are back on the list</h1>
        <p className="text-[14px] text-[#667085] leading-relaxed">
          {email ? <>We will email <strong>{email}</strong> again about quotes, orders and refills.</>
                 : "We will email you again about quotes, orders and refills."}
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <CheckCircle2 className="w-9 h-9 text-[#0E9F6E] mx-auto mb-4" />
      <h1 className="text-[21px] font-bold text-[#0B2747] mb-2">You have been unsubscribed</h1>
      <p className="text-[14px] text-[#667085] leading-relaxed">
        {email ? <>We will not send any more emails to <strong>{email}</strong>.</>
               : "We will not send you any more emails."}
      </p>

      <div className="mt-5 rounded-xl bg-[#FEF4E6] border border-[#F7DFC0] px-4 py-3 text-left">
        <p className="text-[13px] text-[#7A4E12] leading-relaxed">
          Worth knowing: this stops everything, including quotes you asked for, prescription
          review updates, and tracking numbers when an order ships. Sign-in codes and password
          resets still work, because you would be locked out of your account otherwise.
        </p>
      </div>

      <button onClick={undo} disabled={busy}
        className="mt-5 h-11 px-5 rounded-xl border border-[#EAECF0] text-[14px] font-bold text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6] inline-flex items-center gap-2 disabled:opacity-60"
        data-testid="unsub-undo">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />} Undo, keep emailing me
      </button>

      <p className="text-[12px] text-[#98A2B3] mt-6">
        Changed your mind later? Call 1-888-827-2717 or email support@pawlinx.com.
      </p>
    </Shell>
  );
}
